// buildSingleTopFocus.ts

import type {
  InsightsJson,
  InsightsRally,
  InsightsShot,
  //   CvJson,
} from "../../shared/types/insightsEtcTypes"; // adjust import path

import type {
  CvJson,
  CvFrame,
  CvShotBallEvent,
} from "../../shared/types/cvJson";

function getTeamForPlayerId(playerId: number): 0 | 1 {
  return playerId <= 1 ? 0 : 1;
}

export type SingleTopFocusCard = {
  label: string;
  displayLabel: string;
  shortInstruction: string;
  whyThisWon: string[];
  evidence: Array<{
    rallyIndex: number;
    shotIndex: number;
    shotNumber: number;
    playerId: number;
    team: 0 | 1;
    startMs: number;
    endMs: number;
    whyItCounts: string;
    shotType: string | null;
    strokeType: string | null;
    strokeSide: string | null;

    qualityOverall: number | null;
    surfacedFacts: string[];
    scoreFactors: FocusScoreFactor[];
    ruleLabel: string | null;
  }>;
  notChosen: Array<{
    label: string;
    note: string;
  }>;
};
export type SingleTopFocusInput = {
  insightsJson: InsightsJson;
  cvJson?: CvJson | null;

  /**
   * Required for a player-specific answer.
   * If omitted, we still compute candidates, but "isMyShot" style scoring
   * will be weaker / generic.
   */
  selectedPlayerId?: number | null;

  /**
   * Optional tuning knobs.
   */
  options?: Partial<FocusScoringOptions>;
};

export type SingleTopFocusResult = {
  topFocus: FocusCandidate | null;
  allCandidates: FocusCandidate[];
  rejectedCandidates: FocusCandidate[];
  summary: string | null;
};

export type FocusCandidateKey =
  | "third_shot_drop_popup"
  | "transition_reset_too_attackable"
  | "attackable_dink_popup"
  //   | "late_speedup_handling"
  | "defensive_reply_sat_up"
  | "pressure_not_neutralized"
  | "missed_attackable_ball"
  | "short_return_pressure"
  | "backhand_counter_breakdown";

export type FocusCandidate = {
  key: FocusCandidateKey;
  label: string;
  shortInstruction: string;
  whyTrainable: string;

  evidence: FocusEvidence[];

  recurrenceScore: number;
  consequenceScore: number;
  leverageScore: number;
  confidenceScore: number;
  trainabilityScore: number;
  totalScore: number;

  whyThisWon: string[];
  whyNotChosenNotes: string[];
};

export type FocusEvidence = {
  rallyIndex: number;
  shotIndex: number;
  playerId: number;
  team: 0 | 1;

  startMs: number;
  endMs: number;

  shotType: string | null;
  strokeType: string | null;
  strokeSide: string | null;

  qualityOverall: number | null;
  leverageWeight: number;
  severity: number;

  whyItCounts: string;
  outcomeWithinWindow: string[];

  surfacedFacts: string[];
  scoreFactors: FocusScoreFactor[];
  ruleLabel: string | null;

  debug?: Record<string, unknown>;
};

export type FocusScoreFactor = {
  label: string;
  value: number;
  detail: string;
};

export type FocusScoringOptions = {
  lookaheadShots: number;

  recurrenceWeight: number;
  consequenceWeight: number;
  leverageWeight: number;
  confidenceWeight: number;
  trainabilityWeight: number;

  popupExploitedBonus: number;
  popupPotentialBonus: number;
  opponentSpeedupBonus: number;
  opponentPutawayBonus: number;
  rallyLostBonus: number;
  finalErrorBonus: number;

  lowQualityThreshold: number;
  mediumQualityThreshold: number;

  evidenceCapPerCandidate: number;
};

const DEFAULT_OPTIONS: FocusScoringOptions = {
  lookaheadShots: 3,

  recurrenceWeight: 1.0,
  consequenceWeight: 1.35,
  leverageWeight: 1.15,
  confidenceWeight: 0.85,
  trainabilityWeight: 0.9,

  popupExploitedBonus: 2.5,
  popupPotentialBonus: 1.0,
  opponentSpeedupBonus: 1.25,
  opponentPutawayBonus: 2.0,
  rallyLostBonus: 1.75,
  finalErrorBonus: 1.25,

  lowQualityThreshold: 0.72,
  mediumQualityThreshold: 0.84,

  evidenceCapPerCandidate: 5,
};

type InternalDerivedShot = {
  rallyIndex: number;
  shotIndex: number;
  rally: InsightsRally;
  shot: InsightsShot;

  playerId: number;
  team: 0 | 1;
  opponentTeam: 0 | 1;
  isSelectedPlayer: boolean;

  shotType: string | null;
  strokeType: string | null;
  strokeSide: string | null;
  qualityOverall: number | null;

  leverageWeight: number;

  popupStatus: "none" | "potential" | "exploited";
  isFaulted: boolean;
  isFinal: boolean;
  isSpeedup: boolean;
  isPutaway: boolean;

  hitterZone: "deep" | "mid" | "short" | "kitchen" | "unknown";
  ballEndZone: "deep" | "mid" | "short" | "kitchen" | "net" | "unknown";
  ballHeightOverNet: number | null;
  movementMagnitude: number | null;

  cvAttackableBallScore: number;
  cvAttackableBallReasons: string[];
  cvBallPeakHeight: number | null;
  cvBallHeightOverNet: number | null;
  cvBallSpeed: number | null;

  opponentSpeedupWithinWindow: boolean;
  opponentPutawayWithinWindow: boolean;
  opponentWinsRally: boolean;
  selectedPlayerLosesRally: boolean;
  finalErrorWithinWindow: boolean;
};

export function buildSingleTopFocusCard(
  input: SingleTopFocusInput,
): SingleTopFocusCard | null {
  const result = buildSingleTopFocus(input);
  const top = result.topFocus;
  if (!top) return null;

  return {
    label: top.label,
    displayLabel: deriveDisplayLabel(top),
    shortInstruction: top.shortInstruction,
    whyThisWon: top.whyThisWon,
    evidence: top.evidence.map((e) => {
      const whyBits = dedupeParts([e.whyItCounts, ...e.outcomeWithinWindow]);

      const surfacedFacts = dedupeParts(e.surfacedFacts);

      return {
        rallyIndex: e.rallyIndex,
        shotIndex: e.shotIndex,
        shotNumber: e.shotIndex + 1,
        playerId: e.playerId,
        team: e.team,
        startMs: e.startMs,
        endMs: e.endMs,
        shotType: e.shotType,
        strokeType: e.strokeType,
        strokeSide: e.strokeSide,
        qualityOverall: e.qualityOverall,
        surfacedFacts: surfacedFacts,
        scoreFactors: e.scoreFactors,
        ruleLabel: e.ruleLabel,
        whyItCounts: whyBits.join(" • "), // [e.whyItCounts, ...e.outcomeWithinWindow].join(" • "),
      };
    }),
    notChosen: result.rejectedCandidates.slice(0, 3).map((c) => ({
      label: c.label,
      note:
        c.whyNotChosenNotes[0] ??
        "Secondary theme, but not the strongest next-session priority.",
    })),
  };
}

export function buildSingleTopFocus(
  input: SingleTopFocusInput,
): SingleTopFocusResult {
  const options: FocusScoringOptions = {
    ...DEFAULT_OPTIONS,
    ...(input.options ?? {}),
  };

  const derivedShots = buildDerivedShots({
    insightsJson: input.insightsJson,
    cvJson: input.cvJson,
    selectedPlayerId: input.selectedPlayerId ?? null,
    options,
  });

  const candidates = initializeCandidates();

  for (const ds of derivedShots) {
    // We only want player-specific coaching if selectedPlayerId is present.
    // Otherwise, this becomes more generic / less trustworthy.
    if (input.selectedPlayerId != null && !ds.isSelectedPlayer) continue;

    maybeAddThirdShotDropPopupEvidence(candidates, ds, options);
    maybeAddTransitionResetEvidence(candidates, ds, options);
    maybeAddAttackableDinkPopupEvidence(candidates, ds, options);
    maybeAddLateSpeedupHandlingEvidence(candidates, ds, options);
    maybeAddShortReturnPressureEvidence(candidates, ds, options);
    maybeAddBackhandCounterBreakdownEvidence(candidates, ds, options);
  }

  const finalized = Object.values(candidates)
    .map((candidate) => finalizeCandidate(candidate, options))
    .filter((candidate) => candidate.evidence.length > 0)
    .sort((a, b) => b.totalScore - a.totalScore);

  const topFocus = finalized[0] ?? null;
  const rejectedCandidates = finalized.slice(1);

  if (!topFocus) {
    return {
      topFocus: null,
      allCandidates: [],
      rejectedCandidates: [],
      summary: null,
    };
  }

  populateWhyThisWon(topFocus, rejectedCandidates);

  return {
    topFocus,
    allCandidates: finalized,
    rejectedCandidates,
    summary: buildSummary(topFocus, rejectedCandidates),
  };
}

function buildDerivedShots(args: {
  insightsJson: InsightsJson;
  cvJson?: CvJson | null;
  selectedPlayerId: number | null;
  options: FocusScoringOptions;
}): InternalDerivedShot[] {
  const { insightsJson, cvJson, selectedPlayerId, options } = args;
  const output: InternalDerivedShot[] = [];

  for (
    let rallyIndex = 0;
    rallyIndex < insightsJson.rallies.length;
    rallyIndex++
  ) {
    const rally = insightsJson.rallies[rallyIndex];
    const shots = rally.shots ?? [];
    const winningTeam = rally.winning_team;
    const leverageWeight = computeLeverageWeight(rally);

    for (let shotIndex = 0; shotIndex < shots.length; shotIndex++) {
      const shot = shots[shotIndex];
      const playerId = shot.player_id;
      const team = getTeamForPlayerId(playerId); //playerId <= 1 ? 0 : 1;
      const opponentTeam = team === 0 ? 1 : 0;

      const nextShots = shots.slice(
        shotIndex + 1,
        shotIndex + 1 + options.lookaheadShots,
      );

      const popupStatus = normalizePopupStatus(shot);
      const shotType = safeString(shot.shot_type);
      const strokeType = safeString(shot.stroke_type);
      const strokeSide = safeString(shot.stroke_side);
      const qualityOverall =
        typeof shot.quality?.overall === "number" ? shot.quality.overall : null;

      const movementMagnitude = magnitude2d(
        shot.shooter_movement_from_last_shot,
      );
      const hitterZone = inferHitterZone(shot, playerId);
      const ballEndZone = normalizeBallEndZone(shot);
      const ballHeightOverNet =
        typeof shot.resulting_ball_movement?.height_over_net === "number"
          ? shot.resulting_ball_movement.height_over_net
          : null;
      const cvAttackableBall = deriveCvAttackableBallSignal({
        cvJson,
        rallyIndex,
        shotIndex,
      });

      const opponentSpeedupWithinWindow = nextShots.some((s) => {
        // const sTeam = s.player_id <= 1 ? 0 : 1;
        const sTeam = getTeamForPlayerId(s.player_id);
        return sTeam === opponentTeam && !!s.is_speedup;
      });
      const opponentPutawayWithinWindow = nextShots.some((s) => {
        // const sTeam = s.player_id <= 1 ? 0 : 1;
        const sTeam = getTeamForPlayerId(s.player_id);
        return (
          sTeam === opponentTeam &&
          (!!s.is_putaway || safeString(s.winner_type) === "forced_fault")
        );
      });

      const finalErrorWithinWindow = nextShots.some(
        (s) => !!s.is_final && hasAnyFault(s),
      );
      const opponentWinsRally =
        winningTeam != null ? winningTeam === opponentTeam : false;
      const selectedPlayerLosesRally =
        selectedPlayerId != null
          ? winningTeam != null &&
            (selectedPlayerId <= 1 ? 0 : 1) !== winningTeam
          : false;

      output.push({
        rallyIndex,
        shotIndex,
        rally,
        shot,

        playerId,
        team,
        opponentTeam,
        isSelectedPlayer:
          selectedPlayerId != null && playerId === selectedPlayerId,

        shotType,
        strokeType,
        strokeSide,
        qualityOverall,

        leverageWeight,

        popupStatus,
        isFaulted: hasAnyFault(shot),
        isFinal: !!shot.is_final,
        isSpeedup: !!shot.is_speedup,
        isPutaway: !!shot.is_putaway,

        hitterZone,
        ballEndZone,
        ballHeightOverNet,
        movementMagnitude,
        cvAttackableBallScore: cvAttackableBall.score,
        cvAttackableBallReasons: cvAttackableBall.reasons,
        cvBallPeakHeight: cvAttackableBall.peakHeight,
        cvBallHeightOverNet: cvAttackableBall.heightOverNet,
        cvBallSpeed: cvAttackableBall.speed,

        opponentSpeedupWithinWindow,
        opponentPutawayWithinWindow,
        opponentWinsRally,
        selectedPlayerLosesRally,
        finalErrorWithinWindow,
      });
    }
  }

  return output;
}

function initializeCandidates(): Record<FocusCandidateKey, FocusCandidate> {
  return {
    third_shot_drop_popup: makeEmptyCandidate(
      "third_shot_drop_popup",
      "Third-shot drops are sitting up too much",
      "Prioritize lower third-shot drops that stay below the opponent's strike zone.",
      "This is trainable immediately through feed + live third-shot drop reps.",
    ),
    transition_reset_too_attackable: makeEmptyCandidate(
      "transition_reset_too_attackable",
      "Transition resets are sitting up under pressure",
      "Prioritize safer transition reset shape and depth under pace.",
      "This is a classic next-session drill focus and usually changes outcomes quickly.",
    ),
    attackable_dink_popup: makeEmptyCandidate(
      "attackable_dink_popup",
      "Some dinks are sitting up",
      "Prioritize less-lifted dinks and fewer popups at the line.",
      "This is trainable through controlled kitchen reps and pattern recognition.",
    ),

    defensive_reply_sat_up: makeEmptyCandidate(
      "defensive_reply_sat_up",
      "Defensive replies are sitting up too much",
      "Prioritize lower defensive replies under pressure.",
      "This is concrete and highly drillable with pressure feeds and reset/block reps.",
    ),
    pressure_not_neutralized: makeEmptyCandidate(
      "pressure_not_neutralized",
      "Replies under pressure are not stopping the attack",
      "Prioritize replies that actually slow the exchange and force one more neutral ball.",
      "This is specific enough to train in the next session.",
    ),

    missed_attackable_ball: makeEmptyCandidate(
      "missed_attackable_ball",
      "Attackable balls are being passed up or neutralized",
      "Prioritize recognizing balls that sit up and taking them sooner.",
      "This can improve quickly with cueing and pattern reps.",
    ),
    short_return_pressure: makeEmptyCandidate(
      "short_return_pressure",
      "Short returns are inviting immediate pressure",
      "Prioritize deeper returns that do not sit up for the serving team.",
      "This is one of the most straightforward next-session adjustments.",
    ),
    backhand_counter_breakdown: makeEmptyCandidate(
      "backhand_counter_breakdown",
      "Backhand counters/bails are breaking down under pressure",
      "Prioritize more stable backhand counter and bailout shape.",
      "This is a focused technical rep category, not a vague mental theme.",
    ),
  };
}

function makeEmptyCandidate(
  key: FocusCandidateKey,
  label: string,
  shortInstruction: string,
  whyTrainable: string,
): FocusCandidate {
  return {
    key,
    label,
    shortInstruction,
    whyTrainable,
    evidence: [],
    recurrenceScore: 0,
    consequenceScore: 0,
    leverageScore: 0,
    confidenceScore: 0,
    trainabilityScore: 0,
    totalScore: 0,
    whyThisWon: [],
    whyNotChosenNotes: [],
  };
}

function maybeAddThirdShotDropPopupEvidence(
  candidates: Record<FocusCandidateKey, FocusCandidate>,
  ds: InternalDerivedShot,
  options: FocusScoringOptions,
): void {
  if (ds.shotIndex !== 2) return;
  if (ds.shotType !== "drop") return;

  let severity = 0;
  const outcomeBits: string[] = [];
  let why = "";

  const cvAttackable = isCvAttackableBall(ds);

  if (ds.popupStatus === "exploited") {
    severity += options.popupExploitedBonus;
    outcomeBits.push("popup exploited");
    why = "Third-shot drop popped up and was explicitly exploitable.";
  } else if (
    cvAttackable &&
    (ds.opponentSpeedupWithinWindow ||
      ds.opponentPutawayWithinWindow ||
      ds.opponentWinsRally)
  ) {
    severity += 1.65 + Math.min(1.0, ds.cvAttackableBallScore * 0.25);
    outcomeBits.push("CV: high/slow ball flight");
    if (ds.opponentSpeedupWithinWindow)
      outcomeBits.push("opponent attacked soon after");
    if (ds.opponentPutawayWithinWindow)
      outcomeBits.push("opponent putaway followed");
    if (ds.opponentWinsRally) outcomeBits.push("rally lost");
    why =
      "Third-shot drop appeared to sit up from the CV ball flight and led to pressure.";
  } else if (
    ds.qualityOverall != null &&
    ds.qualityOverall < options.lowQualityThreshold &&
    (ds.opponentSpeedupWithinWindow ||
      ds.opponentPutawayWithinWindow ||
      ds.opponentWinsRally)
  ) {
    severity += 1.75;
    if (ds.opponentSpeedupWithinWindow)
      outcomeBits.push("opponent attacked soon after");
    if (ds.opponentPutawayWithinWindow)
      outcomeBits.push("opponent putaway followed");
    if (ds.opponentWinsRally) outcomeBits.push("rally lost");
    why = "Third-shot drop quality was low and it led to immediate pressure.";
  } else {
    return;
  }

  pushEvidence(
    candidates.third_shot_drop_popup,
    makeEvidence(ds, severity, why, outcomeBits),
  );
}

function maybeAddTransitionResetEvidence(
  candidates: Record<FocusCandidateKey, FocusCandidate>,
  ds: InternalDerivedShot,
  options: FocusScoringOptions,
): void {
  const resetLike =
    ds.shotType === "drop" ||
    ds.shot.shot_type === "drop" ||
    !!ds.shot.is_reset ||
    ds.shot.vertical_type === "dig";

  const inTransition = ds.hitterZone === "mid" || ds.hitterZone === "short";
  if (!resetLike || !inTransition) return;

  let severity = 0;
  const outcomeBits: string[] = [];

  if (ds.popupStatus === "exploited") {
    severity += options.popupExploitedBonus;
    outcomeBits.push("popup exploited");
  }
  if (isCvAttackableBall(ds)) {
    severity += 1.2 + Math.min(0.8, ds.cvAttackableBallScore * 0.2);
    outcomeBits.push("CV: high/slow ball flight");
  }
  if (ds.opponentSpeedupWithinWindow) {
    severity += options.opponentSpeedupBonus;
    outcomeBits.push("opponent speedup followed");
  }
  if (ds.opponentPutawayWithinWindow) {
    severity += options.opponentPutawayBonus;
    outcomeBits.push("opponent putaway followed");
  }
  if (ds.opponentWinsRally) {
    severity += options.rallyLostBonus;
    outcomeBits.push("rally lost");
  }

  if (
    severity <= 0 &&
    ds.qualityOverall != null &&
    ds.qualityOverall < options.lowQualityThreshold
  ) {
    severity += 1.25;
    outcomeBits.push("low-quality reset-like ball");
  }

  if (severity <= 0) return;

  pushEvidence(
    candidates.transition_reset_too_attackable,
    makeEvidence(
      ds,
      severity,
      "A reset-like ball from transition appears to have sat up and led to pressure.",
      outcomeBits,
    ),
  );
}

function maybeAddAttackableDinkPopupEvidence(
  candidates: Record<FocusCandidateKey, FocusCandidate>,
  ds: InternalDerivedShot,
  options: FocusScoringOptions,
): void {
  if (ds.shotType !== "dink") return;

  let severity = 0;
  const outcomeBits: string[] = [];
  let why = "";

  const cvAttackable = isCvAttackableBall(ds);

  if (ds.popupStatus === "exploited") {
    severity += options.popupExploitedBonus;
    outcomeBits.push("popup exploited");
    why = "Dink popped up and was punished.";
  } else if (cvAttackable) {
    severity += 1.2 + Math.min(0.8, ds.cvAttackableBallScore * 0.2);
    outcomeBits.push("CV: high/slow ball flight");
    why = "Dink appeared to sit up from the CV ball flight.";
  } else if (ds.popupStatus === "potential") {
    severity += options.popupPotentialBonus;
    outcomeBits.push("potential popup");
    why = "Dink appeared to sit up even if it was not fully punished.";
  }

  if (ds.opponentSpeedupWithinWindow) {
    severity += options.opponentSpeedupBonus;
    outcomeBits.push("opponent speedup followed");
  }
  if (ds.opponentPutawayWithinWindow) {
    severity += options.opponentPutawayBonus;
    outcomeBits.push("opponent putaway followed");
  }
  if (ds.opponentWinsRally) {
    severity += 0.8;
    outcomeBits.push("rally lost");
  }

  if (severity <= 0) return;

  pushEvidence(
    candidates.attackable_dink_popup,
    makeEvidence(
      ds,
      severity,
      why || "Attackable dink invited pressure.",
      outcomeBits,
    ),
  );
}

function maybeAddLateSpeedupHandlingEvidence(
  candidates: Record<FocusCandidateKey, FocusCandidate>,
  ds: InternalDerivedShot,
  options: FocusScoringOptions,
): void {
  const likelyDefenseTouch =
    ds.hitterZone === "short" ||
    ds.hitterZone === "mid" ||
    ds.shot.vertical_type === "dig";

  if (!likelyDefenseTouch) return;

  const lowQuality =
    ds.qualityOverall != null &&
    ds.qualityOverall < options.lowQualityThreshold;

  const putawayLike = ds.opponentPutawayWithinWindow;
  const pressureContinued =
    ds.opponentSpeedupWithinWindow || ds.opponentWinsRally;

  // 1) weak defensive ball that got punished
  const cvAttackable = isCvAttackableBall(ds);

  if ((lowQuality || cvAttackable) && putawayLike) {
    const outcomeBits: string[] = [
      lowQuality
        ? "low-quality defensive reply"
        : "CV: high/slow ball flight",
      "opponent putaway followed",
    ];
    if (ds.opponentWinsRally) outcomeBits.push("rally lost");

    pushEvidence(
      candidates.defensive_reply_sat_up,
      makeEvidence(
        ds,
        1.0 +
          options.opponentPutawayBonus +
          (cvAttackable ? 0.8 : 0) +
          (ds.opponentWinsRally ? 0.9 : 0),
        cvAttackable
          ? "A defensive reply appeared to sit up from the CV ball flight, and the opponent finished soon after."
          : "A low-quality defensive reply sat up enough for the opponent to finish.",
        outcomeBits,
      ),
    );
    return;
  }

  // 2) reply sat up or otherwise did not stop the pressure
  if (lowQuality || cvAttackable || pressureContinued) {
    const outcomeBits: string[] = [];
    if (lowQuality) outcomeBits.push("low-quality defensive reply");
    if (cvAttackable) outcomeBits.push("CV: high/slow ball flight");
    if (ds.opponentSpeedupWithinWindow)
      outcomeBits.push("opponent speedup followed");
    if (ds.opponentWinsRally) outcomeBits.push("rally lost");

    pushEvidence(
      candidates.pressure_not_neutralized,
      makeEvidence(
        ds,
        (lowQuality ? 1.0 : 0) +
          (cvAttackable ? 1.1 : 0) +
          (ds.opponentSpeedupWithinWindow ? options.opponentSpeedupBonus : 0) +
          (ds.opponentWinsRally ? 0.9 : 0),
        cvAttackable
          ? "The reply under pressure appeared to sit up from the CV ball flight."
          : "The reply under pressure did not stop the next attack.",
        outcomeBits,
      ),
    );
  }
}

function maybeAddShortReturnPressureEvidence(
  candidates: Record<FocusCandidateKey, FocusCandidate>,
  ds: InternalDerivedShot,
  options: FocusScoringOptions,
): void {
  // Return is the second shot of the rally.
  if (ds.shotIndex !== 1) return;

  const deepEnough =
    ds.ballEndZone === "deep" ||
    (ds.shot.resulting_ball_movement?.distance_from_baseline ?? 999) < 6;

  if (deepEnough) return;

  let severity = 0;
  const outcomeBits: string[] = [];

  if (ds.opponentSpeedupWithinWindow) {
    severity += options.opponentSpeedupBonus;
    outcomeBits.push("serving team attacked quickly");
  }
  if (ds.opponentPutawayWithinWindow) {
    severity += options.opponentPutawayBonus;
    outcomeBits.push("serving team converted pressure");
  }
  if (ds.opponentWinsRally) {
    severity += 1.0;
    outcomeBits.push("returning team lost rally");
  }

  if (severity <= 0) return;

  pushEvidence(
    candidates.short_return_pressure,
    makeEvidence(
      ds,
      severity,
      "Return did not get deep enough and invited early pressure.",
      outcomeBits,
    ),
  );
}

function maybeAddBackhandCounterBreakdownEvidence(
  candidates: Record<FocusCandidateKey, FocusCandidate>,
  ds: InternalDerivedShot,
  options: FocusScoringOptions,
): void {
  if (ds.strokeType !== "backhand") return;

  let severity = 0;
  const outcomeBits: string[] = [];

  if (
    ds.qualityOverall != null &&
    ds.qualityOverall < options.lowQualityThreshold
  ) {
    severity += 1.0;
    outcomeBits.push("low-quality backhand reply");
  }
  if (ds.isFaulted || ds.isFinal) {
    severity += options.finalErrorBonus;
    outcomeBits.push("backhand error");
  }
  if (ds.opponentPutawayWithinWindow) {
    severity += options.opponentPutawayBonus;
    outcomeBits.push("opponent finished soon after");
  }
  if (ds.opponentWinsRally) {
    severity += 0.75;
    outcomeBits.push("rally lost");
  }

  if (severity <= 0) return;

  pushEvidence(
    candidates.backhand_counter_breakdown,
    makeEvidence(
      ds,
      severity,
      "Backhand bailout/counter quality broke down under pressure.",
      outcomeBits,
    ),
  );
}

function pushEvidence(
  candidate: FocusCandidate,
  evidence: FocusEvidence,
): void {
  candidate.evidence.push(evidence);

  candidate.recurrenceScore += 1;
  candidate.consequenceScore += evidence.severity;
  candidate.leverageScore += evidence.leverageWeight;
  candidate.confidenceScore += deriveEvidenceConfidence(evidence);
  candidate.trainabilityScore += 1;
}

function finalizeCandidate(
  candidate: FocusCandidate,
  options: FocusScoringOptions,
): FocusCandidate {
  const sortedEvidence = [...candidate.evidence]
    .sort((a, b) => {
      const aScore = a.severity * a.leverageWeight;
      const bScore = b.severity * b.leverageWeight;
      return bScore - aScore;
    })
    .slice(0, options.evidenceCapPerCandidate);

  const recurrenceComponent =
    candidate.recurrenceScore * options.recurrenceWeight;
  const consequenceComponent =
    candidate.consequenceScore * options.consequenceWeight;
  const leverageComponent = candidate.leverageScore * options.leverageWeight;
  const confidenceComponent =
    candidate.confidenceScore * options.confidenceWeight;
  const trainabilityComponent =
    candidate.trainabilityScore * options.trainabilityWeight;

  const totalScore =
    recurrenceComponent +
    consequenceComponent +
    leverageComponent +
    confidenceComponent +
    trainabilityComponent;

  return {
    ...candidate,
    evidence: sortedEvidence,
    totalScore,
  };
}

function populateWhyThisWon(
  winner: FocusCandidate,
  rejected: FocusCandidate[],
): void {
  const avgSeverity =
    winner.evidence.reduce((sum, e) => sum + e.severity, 0) /
    Math.max(1, winner.evidence.length);
  const avgLeverage =
    winner.evidence.reduce((sum, e) => sum + e.leverageWeight, 0) /
    Math.max(1, winner.evidence.length);

  winner.whyThisWon = [
    `${winner.evidence.length} supporting rally moments were strong enough to count.`,
    `Average severity ${fmtNum(avgSeverity, 1)} with leverage ${fmtNum(avgLeverage, 2)} made this stand out.`,
    `It is concrete enough to train in the very next session: ${winner.shortInstruction}`,
  ];

  for (const candidate of rejected) {
    const note =
      candidate.totalScore < winner.totalScore * 0.75
        ? "Present, but less repeated and/or less costly than the winner."
        : "A plausible secondary theme, but it lost on recurrence, consequence, or drillability.";
    candidate.whyNotChosenNotes = [note];
  }
}

function buildSummary(
  winner: FocusCandidate,
  rejected: FocusCandidate[],
): string {
  const second = rejected[0];
  if (!second) {
    return `${winner.label}. This had the clearest repeated evidence and the best next-session drill value.`;
  }

  return `${winner.label}. It beat "${second.label}" because it showed a stronger mix of recurrence, consequence, and next-session trainability.`;
}

function deriveDisplayLabel(candidate: FocusCandidate): string {
  switch (candidate.key) {
    case "pressure_not_neutralized":
      return derivePressureNotNeutralizedLabel(candidate);
    case "defensive_reply_sat_up":
      return deriveDefensiveReplySatUpLabel(candidate);
    default:
      return candidate.label;
  }
}

function derivePressureNotNeutralizedLabel(candidate: FocusCandidate): string {
  const evidence = candidate.evidence;
  if (!evidence.length) return candidate.label;

  const shortOrMidCount = evidence.filter((e) => {
    const zone =
      typeof e.debug?.hitterZone === "string" ? e.debug.hitterZone : null;
    return zone === "short" || zone === "mid";
  }).length;

  const speedupCount = evidence.filter(
    (e) => !!e.debug?.opponentSpeedupWithinWindow,
  ).length;
  const lowQualityCount = evidence.filter(
    (e) =>
      typeof e.qualityOverall === "number" &&
      e.qualityOverall < DEFAULT_OPTIONS.lowQualityThreshold,
  ).length;

  if (
    shortOrMidCount >= Math.ceil(evidence.length / 2) &&
    speedupCount >= Math.ceil(evidence.length / 2)
  ) {
    return "Transition-zone replies are allowing immediate opponent attacks";
  }

  if (
    lowQualityCount >= Math.ceil(evidence.length / 2) &&
    speedupCount >= Math.ceil(evidence.length / 2)
  ) {
    return "Low-quality replies are keeping opponents on offense";
  }

  if (shortOrMidCount >= Math.ceil(evidence.length / 2)) {
    return "Replies from transition zone are sitting up too much";
  }

  return candidate.label;
}

function deriveDefensiveReplySatUpLabel(candidate: FocusCandidate): string {
  const evidence = candidate.evidence;
  if (!evidence.length) return candidate.label;

  const putawayCount = evidence.filter(
    (e) => !!e.debug?.opponentPutawayWithinWindow,
  ).length;
  const popupCount = evidence.filter(
    (e) => e.debug?.popupStatus === "exploited",
  ).length;
  const kitchenishCount = evidence.filter((e) => {
    const zone =
      typeof e.debug?.ballEndZone === "string" ? e.debug.ballEndZone : null;
    return zone === "kitchen" || zone === "short";
  }).length;

  if (
    putawayCount >= Math.ceil(evidence.length / 2) &&
    popupCount >= Math.ceil(evidence.length / 2)
  ) {
    return "Attackable replies are getting punished right away";
  }

  if (putawayCount >= Math.ceil(evidence.length / 2)) {
    return "Defensive replies are sitting up for putaways";
  }

  if (kitchenishCount >= Math.ceil(evidence.length / 2)) {
    return "Short replies are sitting up too much";
  }

  return candidate.label;
}

function dedupeParts(parts: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const out: string[] = [];

  for (const raw of parts) {
    const s = (raw ?? "").trim();
    if (!s) continue;

    const key = s.toLowerCase();
    if (seen.has(key)) continue;

    seen.add(key);
    out.push(s);
  }

  return out;
}

// -----------------------
// Helpers
// -----------------------

function fmtNum(n: number, digits = 2): string {
  return Number.isFinite(n) ? n.toFixed(digits) : "—";
}

function titleCaseLoose(s: string): string {
  return s.replace(/_/g, " ");
}

function buildSurfacedFacts(ds: InternalDerivedShot): string[] {
  const facts: string[] = [];

  if (ds.shotType) facts.push(titleCaseLoose(ds.shotType));
  if (ds.strokeType) facts.push(titleCaseLoose(ds.strokeType));
  if (ds.strokeSide) facts.push(titleCaseLoose(ds.strokeSide));

  if (typeof ds.qualityOverall === "number") {
    facts.push(`Q ${fmtNum(ds.qualityOverall, 2)}`);
  }

  if (ds.hitterZone !== "unknown") {
    facts.push(`from ${ds.hitterZone}`);
  }

  if (ds.ballEndZone !== "unknown") {
    facts.push(`ball to ${ds.ballEndZone}`);
  }

  if (ds.popupStatus !== "none") {
    facts.push(`popup ${ds.popupStatus}`);
  }

  if (ds.isSpeedup) facts.push("speedup shot");
  if (ds.isPutaway) facts.push("putaway shot");
  if (ds.isFinal) facts.push("final shot");
  if (ds.isFaulted) facts.push("fault on shot");

  if (typeof ds.ballHeightOverNet === "number") {
    facts.push(`net clr ${fmtNum(ds.ballHeightOverNet, 1)}`);
  }

  if (typeof ds.cvBallPeakHeight === "number") {
    facts.push(`CV peak ${fmtNum(ds.cvBallPeakHeight, 1)}`);
  }

  if (typeof ds.cvBallHeightOverNet === "number") {
    facts.push(`CV net clr ${fmtNum(ds.cvBallHeightOverNet, 1)}`);
  }

  if (isCvAttackableBall(ds)) {
    facts.push(`CV sat-up cue ${fmtNum(ds.cvAttackableBallScore, 1)}`);
  }

  if (typeof ds.movementMagnitude === "number") {
    facts.push(`move ${fmtNum(ds.movementMagnitude, 1)}`);
  }

  if (ds.opponentSpeedupWithinWindow) {
    facts.push("opp speedup followed");
  }
  if (ds.opponentPutawayWithinWindow) {
    facts.push("opp putaway followed");
  }
  if (ds.opponentWinsRally) {
    facts.push("rally lost");
  }

  return facts;
}

function buildScoreFactors(args: {
  ds: InternalDerivedShot;
  severity: number;
  outcomeWithinWindow: string[];
}): FocusScoreFactor[] {
  const { ds, severity, outcomeWithinWindow } = args;
  const factors: FocusScoreFactor[] = [
    {
      label: "Severity",
      value: severity,
      detail: "How costly or exposed this moment looked.",
    },
    {
      label: "Leverage",
      value: ds.leverageWeight,
      detail: "Score-context multiplier for late or close-game moments.",
    },
  ];

  if (typeof ds.qualityOverall === "number") {
    factors.push({
      label: "Shot quality",
      value: ds.qualityOverall,
      detail: "PB Vision overall quality score for this shot.",
    });
  }

  if (outcomeWithinWindow.length) {
    factors.push({
      label: "Follow-through",
      value: outcomeWithinWindow.length,
      detail: outcomeWithinWindow.join(", "),
    });
  }

  if (ds.popupStatus !== "none") {
    factors.push({
      label: "Popup",
      value: ds.popupStatus === "exploited" ? 2 : 1,
      detail: `PB Vision popup status: ${ds.popupStatus}.`,
    });
  }

  if (isCvAttackableBall(ds)) {
    factors.push({
      label: "CV sat-up cue",
      value: ds.cvAttackableBallScore,
      detail: ds.cvAttackableBallReasons.length
        ? ds.cvAttackableBallReasons.join(", ")
        : "CV ball trajectory had high/slow shape.",
    });
  }

  return factors;
}

function deriveRuleLabel(ds: InternalDerivedShot): string | null {
  if (ds.popupStatus === "exploited") return "popup exploited";
  if (ds.opponentPutawayWithinWindow) return "putaway followed";
  if (ds.opponentSpeedupWithinWindow) return "pressure continued";
  if (isCvAttackableBall(ds)) return "ball may have sat up";
  if (ds.isFaulted) return "fault on shot";
  if (
    ds.qualityOverall != null &&
    ds.qualityOverall < DEFAULT_OPTIONS.lowQualityThreshold
  ) {
    return "low-quality reply";
  }
  return null;
}

function makeEvidence(
  ds: InternalDerivedShot,
  severity: number,
  whyItCounts: string,
  outcomeWithinWindow: string[],
): FocusEvidence {
  return {
    rallyIndex: ds.rallyIndex,
    shotIndex: ds.shotIndex,
    playerId: ds.playerId,
    team: ds.team,
    startMs: ds.shot.start_ms,
    endMs: ds.shot.end_ms,
    shotType: ds.shotType,
    strokeType: ds.strokeType,
    strokeSide: ds.strokeSide,
    qualityOverall: ds.qualityOverall,
    leverageWeight: ds.leverageWeight,
    severity,
    whyItCounts,
    outcomeWithinWindow,
    surfacedFacts: buildSurfacedFacts(ds),
    scoreFactors: buildScoreFactors({ ds, severity, outcomeWithinWindow }),
    ruleLabel: deriveRuleLabel(ds),
    debug: {
      popupStatus: ds.popupStatus,
      hitterZone: ds.hitterZone,
      ballEndZone: ds.ballEndZone,
      ballHeightOverNet: ds.ballHeightOverNet,
      cvAttackableBallScore: ds.cvAttackableBallScore,
      cvAttackableBallReasons: ds.cvAttackableBallReasons,
      cvBallPeakHeight: ds.cvBallPeakHeight,
      cvBallHeightOverNet: ds.cvBallHeightOverNet,
      cvBallSpeed: ds.cvBallSpeed,
      movementMagnitude: ds.movementMagnitude,
      isFaulted: ds.isFaulted,
      isFinal: ds.isFinal,
      isSpeedup: ds.isSpeedup,
      isPutaway: ds.isPutaway,
      opponentSpeedupWithinWindow: ds.opponentSpeedupWithinWindow,
      opponentPutawayWithinWindow: ds.opponentPutawayWithinWindow,
      opponentWinsRally: ds.opponentWinsRally,
      finalErrorWithinWindow: ds.finalErrorWithinWindow,
    },
  };
}

function computeLeverageWeight(rally: InsightsRally): number {
  const runningScore = rally.scoring_info?.running_score;
  if (!runningScore) return 1.0;

  const [a, b] = runningScore;
  const maxScore = Math.max(a, b);
  const margin = Math.abs(a - b);

  let weight = 1.0;

  if (maxScore >= 9) weight += 0.4;
  if (maxScore >= 10) weight += 0.25;
  if (margin <= 2) weight += 0.2;

  return weight;
}

function normalizePopupStatus(
  shot: InsightsShot,
): "none" | "potential" | "exploited" {
  const popup = (shot.errors as { popup?: unknown } | undefined)?.popup;
  if (popup === "exploited") return "exploited";
  if (popup === "potential") return "potential";
  return "none";
}

type CvAttackableBallSignal = {
  score: number;
  reasons: string[];
  peakHeight: number | null;
  heightOverNet: number | null;
  speed: number | null;
};

function emptyCvAttackableBallSignal(): CvAttackableBallSignal {
  return {
    score: 0,
    reasons: [],
    peakHeight: null,
    heightOverNet: null,
    speed: null,
  };
}

function deriveCvAttackableBallSignal(args: {
  cvJson?: CvJson | null;
  rallyIndex: number;
  shotIndex: number;
}): CvAttackableBallSignal {
  const { cvJson, rallyIndex, shotIndex } = args;
  const shotEvent = getCvShotEvent({ cvJson, rallyIndex, shotIndex });
  if (!shotEvent) return emptyCvAttackableBallSignal();

  const heightOverNet =
    typeof shotEvent.ball_height_over_net === "number"
      ? shotEvent.ball_height_over_net
      : null;
  const peakHeight =
    typeof shotEvent.ball_peak?.z === "number" ? shotEvent.ball_peak.z : null;
  const speed =
    typeof shotEvent.speed === "number"
      ? shotEvent.speed
      : typeof shotEvent.path_average_speed === "number"
        ? shotEvent.path_average_speed
        : null;

  const reasons: string[] = [];
  let score = 0;

  if (heightOverNet != null && heightOverNet >= 3.2) {
    score += 1.15;
    reasons.push(`high over the net (${fmtNum(heightOverNet, 1)} ft)`);
  }

  if (peakHeight != null && peakHeight >= 4.0) {
    score += 1.0;
    reasons.push(`high peak (${fmtNum(peakHeight, 1)} ft)`);
  }

  if (speed != null && speed <= 18) {
    score += 0.55;
    reasons.push(`slow ball speed (${fmtNum(speed, 1)} mph)`);
  }

  if (
    heightOverNet != null &&
    peakHeight != null &&
    peakHeight - heightOverNet >= 0.9
  ) {
    score += 0.4;
    reasons.push("loopy trajectory");
  }

  return {
    score,
    reasons,
    peakHeight,
    heightOverNet,
    speed,
  };
}

function getCvShotEvent(args: {
  cvJson?: CvJson | null;
  rallyIndex: number;
  shotIndex: number;
}): CvShotBallEvent | null {
  const frames = getCvRallyFrames(args.cvJson, args.rallyIndex);
  if (!frames) return null;

  let currentShotIndex = -1;
  for (const frame of frames) {
    const shot = frame?.balls?.shot;
    if (!shot) continue;

    currentShotIndex += 1;
    if (currentShotIndex === args.shotIndex) return shot;
  }

  return null;
}

function getCvRallyFrames(
  cvJson: CvJson | null | undefined,
  rallyIndex: number,
): CvFrame[] | null {
  const frames = cvJson?.sessions?.[0]?.rallies?.[rallyIndex]?.frames;
  return Array.isArray(frames) && frames.length ? frames : null;
}

function isCvAttackableBall(ds: InternalDerivedShot): boolean {
  return ds.cvAttackableBallScore >= 1.65;
}

// function hasAnyFault(shot: InsightsShot): boolean {
//   const faults = shot.errors?.faults;
//   if (!faults) return false;
//   return Object.values(faults).some((v) => v === true);
// }

function hasAnyFault(shot: InsightsShot): boolean {
  const faults = shot.errors?.faults;
  if (!faults) return false;

  return Object.values(faults).some((v) => {
    if (v === true) return true;
    if (v && typeof v === "object") return true;
    return false;
  });
}

function inferHitterZone(
  shot: InsightsShot,
  playerId: number,
): "deep" | "mid" | "short" | "kitchen" | "unknown" {
  const pos = shot.player_positions?.[playerId];
  if (!pos) return "unknown";

  // Assumes y roughly runs baseline-to-baseline with far team on low y and near team on high y.
  // These thresholds are intentionally rough for first pass.
  const y = pos.y;

  if (playerId <= 1) {
    if (y < 4) return "deep";
    if (y < 11) return "mid";
    if (y < 15) return "short";
    return "kitchen";
  }

  if (y > 40) return "deep";
  if (y > 33) return "mid";
  if (y > 29) return "short";
  return "kitchen";
}

function normalizeBallEndZone(
  shot: InsightsShot,
): "deep" | "mid" | "short" | "kitchen" | "net" | "unknown" {
  const zone = shot.resulting_ball_movement?.trajectory?.end?.zone;
  if (
    zone === "deep" ||
    zone === "mid" ||
    zone === "short" ||
    zone === "kitchen" ||
    zone === "net"
  ) {
    return zone;
  }
  return "unknown";
}

function magnitude2d(vec: { x: number; y: number } | undefined): number | null {
  if (!vec) return null;
  return Math.sqrt(vec.x * vec.x + vec.y * vec.y);
}

function deriveEvidenceConfidence(evidence: FocusEvidence): number {
  let score = 1.0;
  if (evidence.qualityOverall != null) score += 0.2;
  if (evidence.outcomeWithinWindow.length > 0) score += 0.3;
  if (evidence.leverageWeight > 1.2) score += 0.15;
  return score;
}

function safeString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}
